/**
 * Click-time standing-rule overlap — same demonstrable predicate the
 * pipeline uses at append (`pipeline/rules_engine.py` /
 * `rules_demonstrably_overlap`).
 *
 * The Worker cannot read `rules.yaml`. It compares a would-be queue mint
 * against (1) `deriveRulesView` (applied `rule_change_requests`) and
 * (2) reconstructed mints from earlier `make_rule=1` queue rows whose
 * `make_rule_outcome` is `pending` or `created` (R32A-1). Rows the
 * pipeline marked `not_created`, and pre-column NULL rows, are not
 * live rules. Undecidable pairs are NOT a click-time refusal — the
 * pipeline remains the authoritative backstop. Do not weaken that
 * backstop to "tell the AM".
 */

import {
  decodeRuleAmount,
  readRuleAmount,
  ruleAmountWords,
} from "./rule_amount_spec";

export type OverlapKind = "none" | "demonstrable" | "undecidable";

export type OverlapMatch = {
  name_regex?: string;
  merchant_regex?: string;
  account_last4?: string;
  amount?: string;
};

export type OverlapRule = {
  id: number | null;
  active: boolean;
  match: OverlapMatch;
  post: { account: string; location: string };
  source: "applied" | "queue";
  queueId?: number;
};

export type ExistingStandingRuleDetails = {
  id: number | null;
  source: "applied" | "queue";
  queueId?: number;
  account: string;
  location: string;
  amount: string | null;
  matchText: string;
  kind?: "posting" | "vendor_identity";
};

// CPython 3.12 `re.escape` specials (bytes map on `()[]{}?*+-|^$\\.&~#` plus
// whitespace). Queue mint always goes through this so click-time regexes
// match `vendor_scope_name_regex` / amount-pin in `pipeline/rules_apply.py`.
const PYTHON_RE_ESCAPE = new Set<string>([
  "(",
  ")",
  "[",
  "]",
  "{",
  "}",
  "?",
  "*",
  "+",
  "-",
  "|",
  "^",
  "$",
  "\\",
  ".",
  "&",
  "~",
  "#",
  " ",
  "\t",
  "\n",
  "\r",
  "\v",
  "\f",
]);

const INLINE_FLAGS = /^\(\?[aiLmsux]+\)/;
const REGEX_META = new Set<string>(".^$*+?{}[]|()".split(""));
// Invert every character `re.escape` emits (PYTHON_RE_ESCAPE). Queue mints
// put `\\ ` around spaces in "SYN OVERLAP HARDWARE". Python's
// `literal_regex_probe` inverts the same set (derived from `re.escape` at
// import — R32R-1). Unknown escapes (`\\d`, `\\s`, `\\w`) stay null.
const LITERAL_ESCAPES = new Set<string>([...PYTHON_RE_ESCAPE, ...REGEX_META]);

function isWordChar(char: string): boolean {
  return /[\p{L}\p{N}_]/u.test(char);
}

export function escapeRegexPython(text: string): string {
  let out = "";
  for (const char of text) {
    out += PYTHON_RE_ESCAPE.has(char) ? `\\${char}` : char;
  }
  return out;
}

/** GRANT (16) vendor-scope mint. Lockstep with `vendor_scope_name_regex`. */
export function vendorScopeNameRegex(matchText: string): string {
  const lead = matchText.length > 0 && isWordChar(matchText[0] ?? "") ? "\\b" : "";
  const last = matchText[matchText.length - 1] ?? "";
  const tail = matchText.length > 0 && isWordChar(last) ? "\\b" : "";
  return `(?i)${lead}${escapeRegexPython(matchText)}${tail}`;
}

/** Amount-pinned mint: unanchored escaped literal, `(?i)` prefix. */
export function amountPinNameRegex(matchText: string): string {
  return `(?i)${escapeRegexPython(matchText)}`;
}

export function matchTextFromQueueRow(
  merchant: string | null | undefined,
  descriptor: string | null | undefined,
  chosenMatchText?: string | null,
): string {
  const chosen = chosenMatchText?.trim() ?? "";
  if (chosen.length > 0) return chosen;
  const merchantText = merchant?.trim() ?? "";
  if (merchantText.length > 0) return merchantText;
  return descriptor?.trim() ?? "";
}

/**
 * Pattern the pipeline would write for this queue answer. `null` when there
 * is nothing to mint (empty merchant+descriptor) — click-time skips; the
 * pipeline records debt. Kind A (`vendor_identity`) does not use this
 * posting-rule matcher.
 */
export function buildQueueMintMatch(args: {
  merchant: string | null | undefined;
  descriptor: string | null | undefined;
  amount: string | null | undefined;
  ruleScope: "vendor" | "amount" | null | undefined;
  matchText?: string | null;
}): OverlapMatch | null {
  const matchText = matchTextFromQueueRow(
    args.merchant,
    args.descriptor,
    args.matchText,
  );
  if (matchText.length === 0) return null;
  // R12 A2-8 mirror: `rules_apply.py` writes `merchant_regex` (with no
  // `name_regex`) when a card has a merchant name and the AM chose no
  // `match_text` (`use_merchant`). The Worker's candidate must be built the
  // same way, or the click-time 409 misses the rule the pipeline would mint and
  // the refusal arrives later, at sync, as `make_rule failed`.
  const chosen = args.matchText?.trim() ?? "";
  const merchant = args.merchant?.trim() ?? "";
  const useMerchant = chosen.length === 0 && merchant.length > 0;
  if (args.ruleScope === "vendor") {
    const pattern = vendorScopeNameRegex(matchText);
    return useMerchant
      ? { merchant_regex: pattern }
      : { name_regex: pattern };
  }
  const amount = args.amount?.trim();
  if (amount === undefined || amount.length === 0) return null;
  const pattern = amountPinNameRegex(matchText);
  return useMerchant
    ? { merchant_regex: pattern, amount }
    : { name_regex: pattern, amount };
}

/** Invert a staff-authored regex to a concrete probe, or `null` if not a literal. */
export function literalRegexProbe(pattern: string): string | null {
  let body = pattern;
  while (INLINE_FLAGS.test(body)) {
    body = body.replace(INLINE_FLAGS, "");
  }
  const chars: string[] = [];
  let index = 0;
  while (index < body.length) {
    const char = body[index];
    if (char === undefined) break;
    if (char === "\\") {
      const escaped = body[index + 1];
      if (escaped === undefined) return null;
      if (escaped === "b") {
        index += 2;
        continue;
      }
      if (LITERAL_ESCAPES.has(escaped)) {
        chars.push(escaped);
        index += 2;
        continue;
      }
      return null;
    }
    if (REGEX_META.has(char)) return null;
    chars.push(char);
    index += 1;
  }
  if (chars.length === 0) return null;
  return chars.join("");
}

function stripInlineFlags(pattern: string): { body: string; ignoreCase: boolean } {
  let body = pattern;
  let ignoreCase = false;
  while (INLINE_FLAGS.test(body)) {
    const flags = body.match(INLINE_FLAGS)?.[0] ?? "";
    if (flags.includes("i")) ignoreCase = true;
    body = body.replace(INLINE_FLAGS, "");
  }
  return { body, ignoreCase };
}

/** `re.search` analogue. `null` when the pattern is not JS-compilable. */
export function searchPythonish(pattern: string, text: string): boolean | null {
  const { body, ignoreCase } = stripInlineFlags(pattern);
  try {
    const compiled = new RegExp(body, ignoreCase ? "i" : "");
    return compiled.test(text);
  } catch {
    return null;
  }
}

/** The codec's canonical unsigned magnitude of a transaction amount. */
function txnMagnitude(value: string): string | null {
  const text = value.trim();
  const unsigned = text.startsWith("-") ? text.slice(1) : text;
  try {
    return decodeRuleAmount(unsigned).magnitude;
  } catch {
    return null;
  }
}

function constraintsCompatible(left: OverlapMatch, right: OverlapMatch): boolean {
  if (
    left.account_last4 !== undefined &&
    right.account_last4 !== undefined &&
    left.account_last4 !== right.account_last4
  ) {
    return false;
  }
  const leftAmount = readRuleAmount(left.amount);
  const rightAmount = readRuleAmount(right.amount);
  if (leftAmount === null || rightAmount === null) return false;
  if (
    leftAmount.magnitude !== null &&
    rightAmount.magnitude !== null &&
    leftAmount.magnitude !== rightAmount.magnitude
  ) {
    return false;
  }
  // Direction can never conflict today: the frozen rule format has no
  // money-out marker, so the only direction is "in" and an unsigned rule can
  // always be satisfied by the same negative amount. That is exactly the
  // demonstrable pair the old signed-number comparison called "none"
  // (R31O-1). A money-out marker would need a conflict check here.
  return true;
}

type ProbeTxn = {
  name: string;
  merchant: string;
  amount: string;
  last4: string | null;
};

function probeTxnFromRule(
  source: OverlapMatch,
  other: OverlapMatch,
): ProbeTxn | null {
  const nameProbe =
    source.name_regex !== undefined ? literalRegexProbe(source.name_regex) : "";
  if (source.name_regex !== undefined && nameProbe === null) return null;
  const merchantProbe =
    source.merchant_regex !== undefined
      ? literalRegexProbe(source.merchant_regex)
      : "";
  if (source.merchant_regex !== undefined && merchantProbe === null) return null;
  const sourceAmount = readRuleAmount(source.amount);
  const otherAmount = readRuleAmount(other.amount);
  if (sourceAmount === null || otherAmount === null) return null;
  // Amount/last4 may come from `other` only after constraintsCompatible
  // confirmed the pair cannot disagree; the probe carries the direction the
  // rules bind, so an any-size-inflow rule is probed with a negative amount.
  const magnitude =
    sourceAmount.magnitude ?? otherAmount.magnitude ?? "0.01";
  const direction =
    sourceAmount.direction !== "either"
      ? sourceAmount.direction
      : otherAmount.direction;
  const amount = direction === "in" ? `-${magnitude}` : magnitude;
  const last4 = source.account_last4 ?? other.account_last4 ?? null;
  return {
    name: nameProbe ?? "",
    merchant: merchantProbe ?? "",
    amount,
    last4,
  };
}

function ruleMatches(rule: OverlapMatch, txn: ProbeTxn): boolean | null {
  if (rule.name_regex !== undefined) {
    const hit = searchPythonish(rule.name_regex, txn.name);
    if (hit === null) return null;
    if (!hit) return false;
  }
  if (rule.merchant_regex !== undefined) {
    const hit = searchPythonish(rule.merchant_regex, txn.merchant);
    if (hit === null) return null;
    if (!hit) return false;
  }
  if (rule.account_last4 !== undefined && rule.account_last4 !== txn.last4) {
    return false;
  }
  const spec = readRuleAmount(rule.amount);
  if (spec === null) return null;
  if (spec.magnitude !== null) {
    const magnitude = txnMagnitude(txn.amount);
    if (magnitude === null) return null;
    if (magnitude !== spec.magnitude) return false;
  }
  if (spec.direction === "in") {
    const value = Number(txn.amount);
    if (!Number.isFinite(value)) return null;
    // A zero-amount transaction has no direction; it matches neither.
    if (value === 0) return false;
    if (!(value < 0)) return false;
  }
  return true;
}

export function classifyRuleOverlap(
  left: OverlapMatch,
  right: OverlapMatch,
): OverlapKind {
  if (!constraintsCompatible(left, right)) return "none";
  if (left.name_regex === undefined && left.merchant_regex === undefined) {
    return "demonstrable";
  }
  if (right.name_regex === undefined && right.merchant_regex === undefined) {
    return "demonstrable";
  }
  let undecidable = false;
  const pairs: Array<[OverlapMatch, OverlapMatch]> = [
    [left, right],
    [right, left],
  ];
  for (const [source, other] of pairs) {
    const probe = probeTxnFromRule(source, other);
    if (probe === null) {
      undecidable = true;
      continue;
    }
    const leftHit = ruleMatches(left, probe);
    const rightHit = ruleMatches(right, probe);
    if (leftHit === null || rightHit === null) {
      undecidable = true;
      continue;
    }
    if (leftHit && rightHit) return "demonstrable";
  }
  return undecidable ? "undecidable" : "none";
}

export function rulesDemonstrablyOverlap(
  left: OverlapMatch,
  right: OverlapMatch,
): boolean {
  return classifyRuleOverlap(left, right) === "demonstrable";
}

/**
 * Skip a pending queue mint that demonstrably overlaps an already-active
 * applied rule. Terminal refusals (`not_created`) never reach this list
 * — `PRIOR_MAKE_RULE_SQL` filters on outcome. This pass still covers the
 * window where the row is `pending` and an Owner rule already exists.
 */
export function replayQueueMintsOnto(
  live: readonly OverlapRule[],
  historical: readonly OverlapRule[],
): OverlapRule[] {
  const out: OverlapRule[] = [...live];
  for (const row of historical) {
    const ghost = out.some(
      (existing) =>
        existing.active &&
        rulesDemonstrablyOverlap(row.match, existing.match),
    );
    if (ghost) continue;
    out.push(row);
  }
  return out;
}

export function findDemonstrableDuplicate(
  candidate: OverlapMatch,
  live: readonly OverlapRule[],
): OverlapRule | undefined {
  return live.find(
    (existing) =>
      existing.active && rulesDemonstrablyOverlap(candidate, existing.match),
  );
}

export function matchSummary(match: OverlapMatch): string {
  if (match.name_regex !== undefined) {
    const probe = literalRegexProbe(match.name_regex);
    if (probe !== null && probe.length > 0) return probe;
  }
  if (match.merchant_regex !== undefined) {
    const probe = literalRegexProbe(match.merchant_regex);
    if (probe !== null && probe.length > 0) return probe;
  }
  return "an existing pattern";
}

export function existingRuleDetails(rule: OverlapRule): ExistingStandingRuleDetails {
  return {
    id: rule.id,
    source: rule.source,
    ...(rule.queueId !== undefined ? { queueId: rule.queueId } : {}),
    account: rule.post.account,
    location: rule.post.location,
    amount: rule.match.amount ?? null,
    matchText: matchSummary(rule.match),
    kind: "posting",
  };
}

export function existingVendorIdentityDetails(args: {
  queueId: number;
  matchText: string;
}): ExistingStandingRuleDetails {
  return {
    id: null,
    source: "queue",
    queueId: args.queueId,
    account: "",
    location: "",
    amount: null,
    matchText: args.matchText,
    kind: "vendor_identity",
  };
}

/**
 * The 409 is the one Worker face that prints a rule size. The codec takes the
 * caller's money formatter (the portal passes `moneyText`), and the Worker has
 * no shared one, so this renders the codec's canonical magnitude as dollars
 * with two decimals. It is deliberately one small function, not a second
 * currency module: signs never reach it (the codec already removed direction).
 */
function ruleAmountDollars(magnitude: string): string {
  const [whole = "0", fraction = ""] = magnitude.split(".");
  const cents = `${fraction}00`.slice(0, 2);
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return `$${grouped}.${cents}`;
}

export function standingRuleAlreadyExistsMessage(
  details: ExistingStandingRuleDetails,
): string {
  if (details.kind === "vendor_identity") {
    return (
      `This standing rule already exists. It already identifies ` +
      `${details.matchText} as the vendor. Later matches still return to ` +
      `Today for account and location. Untick the standing-rule box to book ` +
      `this transaction without creating another identity.`
    );
  }
  // R12 A2-7: the Worker's 409 text reads the size through the codec too, so
  // it never says "amount -0.00". The portal shows its own described card;
  // this sentence is the fallback for a client that prints the message.
  // Owner decision 7 drops "Rule N" / "Queue item N": the rule is described,
  // not identified by a number, and the size is dollars, not canonical text.
  const amountBit = ruleAmountWords(details.amount, ruleAmountDollars);
  return (
    `This standing rule already exists. It matches ${details.matchText} ` +
    `(${amountBit}) and posts to ${details.account} at ${details.location}. ` +
    "Untick the standing-rule box to book this transaction without creating another rule."
  );
}
